import { useMemo, useEffect, useState } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import ComboLocationMultiselect, { LOCATION_TYPE } from '@features/ComboLocationMultiselect';
import { Select } from '@components/Select';
import { ColorPicker } from '@components/ColorPicker';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { FiltersIntegrationType } from '@constants/integrations';
import { DEFAULT_POPUP_WIDGET_ALIGNMENT } from '@constants/widget';
import { widgetAlignmentOptions, widgetOffsetFieldProps, widgetPositionSchemaFields } from '@helpers/widget-position';
import { widgetsService } from '@services';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import Code from './Code';
import WidgetPreview from './WidgetPreview';
import { Modal, MainContent, Form, SubContent, ReviewsMode, ReviewsModeGroup } from './styles';

const REVIEWS_MODE = {
    TOP_RATED: 'top-rated',
    FEATURED: 'featured',
};

const FEATURED_AVAILABILITY = {
    UNKNOWN: 'unknown',
    AVAILABLE: 'available',
    NONE: 'none',
};

const WIDGET_SORTING_MOST_RECENT = 1;
const FEATURED_PROBE_DELAY_MS = 400;

const reviesToShowOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '8', label: '8' },
    { value: '16', label: '16' },
    { value: '24', label: '24' },
];

const EditModal = ({ modal, setModal, account, view, locations, brands }) => {
    const { t } = useTranslation();
    const { mapSelectedLocationsToFormValue, getLocationsOptions } = useLocations();

    const WidgetsSchema = yup.object().shape({
        locations: yup.array().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATIONS || (locationType === LOCATION_TYPE.BRANDS && !brandAccess),
            then: schema => schema.min(1, 'validation.required'),
        }),
        brands: yup.array().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRANDS && brandAccess,
            then: schema => schema.min(1, 'validation.required'),
        }),
        integrations: yup.array().min(1, 'validation.required'),
        locationType: yup.string(),
        backgroundColor: yup.string().validateColor('validation.hexFormat'),
        ...widgetPositionSchemaFields,
    });

    const resolver = useYupValidationResolver(WidgetsSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        formState: { isValid },
    } = useForm({
        mode: 'onBlur',
        resolver,
        defaultValues: {
            sorting: '0',
            count: '24',
            onlyFeatured: false,
            brands: [],
            locations: [],
            integrations: [],
            backgroundColor: '#F1F1F1',
            alignment: DEFAULT_POPUP_WIDGET_ALIGNMENT,
            offsetX: '',
            offsetY: '',
            locationType: LOCATION_TYPE.BRANDS,
            brandAccess: false,
            selectedLocationsIds: [],
        },
    });

    const brandAccess = watch('brandAccess');
    const selectedLocationsIds = watch('selectedLocationsIds');
    const locationType = watch('locationType');
    const formValues = watch(); // For widget preview

    const [featuredAvailability, setFeaturedAvailability] = useState(FEATURED_AVAILABILITY.UNKNOWN);
    const widgetScopeKey = JSON.stringify({
        companies: (formValues.locations || []).map(item => item?.id || item).filter(Boolean),
        brands: formValues.brands || [],
        channels: formValues.integrations || [],
    });

    useEffect(() => {
        const widgetKey = account?.widgetKey;
        const scope = JSON.parse(widgetScopeKey);
        if (!modal.open || !widgetKey || (!scope.companies.length && !scope.brands.length)) {
            setFeaturedAvailability(FEATURED_AVAILABILITY.UNKNOWN);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            widgetsService
                .getReviews(widgetKey, { ...scope, onlyFeatured: true, count: 1, sorting: WIDGET_SORTING_MOST_RECENT })
                .then(result => {
                    if (cancelled) return;
                    if (!Array.isArray(result?.reviews)) {
                        setFeaturedAvailability(FEATURED_AVAILABILITY.UNKNOWN);
                        return;
                    }
                    const hasFeatured = result.reviews.length > 0;
                    setFeaturedAvailability(hasFeatured ? FEATURED_AVAILABILITY.AVAILABLE : FEATURED_AVAILABILITY.NONE);
                    if (!hasFeatured && getValues('onlyFeatured')) {
                        setValue('onlyFeatured', false, { shouldDirty: true });
                    }
                })
                .catch(() => {
                    if (!cancelled) setFeaturedAvailability(FEATURED_AVAILABILITY.UNKNOWN);
                });
        }, FEATURED_PROBE_DELAY_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [modal.open, account?.widgetKey, widgetScopeKey]);

    const noFeaturedReviews = featuredAvailability === FEATURED_AVAILABILITY.NONE;

    useEffect(() => {
        let availableCompaniesIds = [];
        // INFO: if not location based user or admin
        if (brandAccess) {
            if (locationType === LOCATION_TYPE.BRANDS) {
                availableCompaniesIds = brands
                    .map(item => item.locations)
                    .flat()
                    .map(item => item.id);
                setValue(
                    'brands',
                    brands.map(item => item.id),
                    { shouldValidate: true },
                );
                setValue('locations', [], { shouldValidate: true });
            } else {
                availableCompaniesIds = locations.map(item => item.companyId);
                setValue('locations', mapSelectedLocationsToFormValue(availableCompaniesIds), {
                    shouldValidate: true,
                });
                setValue('brands', [], { shouldValidate: true });
            }
        } else {
            availableCompaniesIds = locations.map(item => item.companyId);
            setValue('locations', mapSelectedLocationsToFormValue(availableCompaniesIds), {
                shouldValidate: true,
            });
            setValue('brands', [], { shouldValidate: true });
        }
        const integrations = [
            ...new Set(
                locations
                    .filter(item => availableCompaniesIds.includes(item.companyId))
                    .map(company => company.profiles)
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];
        // INFO: set selected locations ids for depended fields
        setValue('selectedLocationsIds', availableCompaniesIds, { shouldValidate: true });
        setValue('integrations', integrations, { shouldValidate: true });
    }, [brands, locations, brandAccess, locationType]);

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));

    const integrationsOptions = useMemo(
        () =>
            [
                ...new Set(
                    locations
                        .filter(item => selectedLocationsIds.includes(item.companyId))
                        .map(company => company.profiles)
                        .flat()
                        .filter(p => p.type in FiltersIntegrationType)
                        .map(p => p.type),
                ),
            ].map(type => ({ value: type, label: FiltersIntegrationType[type] })),
        [selectedLocationsIds],
    );

    const onCloseModal = () => setModal({ open: false, type: null, view: view.FORM });

    const handleChangeView = () => setModal({ ...modal, view: modal.view === view.CODE ? view.FORM : view.CODE });

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseModal}
            title={
                modal.view === view.CODE
                    ? t('ShowcaseWidgets.widgetCode')
                    : modal.type === 'popup'
                      ? t('ShowcaseWidgets.popupWidget')
                      : t('ShowcaseWidgets.gridWidget')
            }
            onPrimaryAction={modal.view === view.FORM ? handleChangeView : onCloseModal}
            primaryActionText={modal.view === view.FORM ? t('buttons.getCode') : t('buttons.done')}
            primaryActionDisabled={!isValid}
            onSecondaryAction={modal.view === view.FORM ? null : handleChangeView}
            secondaryActionText={t('buttons.back')}
        >
            <FormProvider {...{ control, setValue, watch, getValues }}>
                <MainContent>
                    {modal.view === view.FORM && modal.open && (
                        <>
                            <Form>
                                <ComboLocationMultiselect
                                    brandsOptions={brandsOptions}
                                    locationsOptions={locationsOptions}
                                    module="reviews"
                                    updatedLocationsCb={locationsIds => {
                                        const integrations = [
                                            ...new Set(
                                                locations
                                                    .filter(item => locationsIds.includes(item.companyId))
                                                    .map(company => company.profiles)
                                                    .flat()
                                                    .filter(p => p.type in FiltersIntegrationType)
                                                    .map(p => p.type),
                                            ),
                                        ];
                                        setValue('integrations', integrations, { shouldValidate: true });
                                    }}
                                />
                                <Controller
                                    control={control}
                                    name="integrations"
                                    render={({ field, fieldState: { error }, ref }) => (
                                        <MultiSelect
                                            {...field}
                                            ref={ref}
                                            onChange={value =>
                                                setValue('integrations', value, { shouldValidate: true })
                                            }
                                            options={integrationsOptions}
                                            label={t('common.reviewSites')}
                                            countable
                                            required
                                            error={error}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="count"
                                    render={({ field, ref }) => (
                                        <Select
                                            {...field}
                                            ref={ref}
                                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                            onChange={({ target: { value } }) => {
                                                setValue('count', value, { shouldDirty: true });
                                            }}
                                            options={reviesToShowOptions}
                                            label={t('ShowcaseWidgets.reviewsToShow')}
                                            fullWidth
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="sorting"
                                    render={({ field, ref }) => (
                                        <Select
                                            {...field}
                                            ref={ref}
                                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                            onChange={({ target: { value } }) => {
                                                setValue('sorting', value, { shouldDirty: true });
                                            }}
                                            options={[
                                                { value: '0', label: t('ShowcaseWidgets.random') },
                                                { value: '1', label: t('ShowcaseWidgets.recent') },
                                            ]}
                                            label={t('ShowcaseWidgets.reviewsSorting')}
                                            fullWidth
                                        />
                                    )}
                                />
                                {modal.type === 'popup' && (
                                    <>
                                        <Controller
                                            control={control}
                                            name="alignment"
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={widgetAlignmentOptions(t)}
                                                    label={t('ShowcaseWidgets.widgetPosition')}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                        {[
                                            { name: 'offsetX', label: t('SettingsChannels.widgetOffsetX') },
                                            { name: 'offsetY', label: t('SettingsChannels.widgetOffsetY') },
                                        ].map(({ name, label }) => (
                                            <Controller
                                                key={name}
                                                control={control}
                                                name={name}
                                                render={({ field, fieldState: { error } }) => (
                                                    <Input
                                                        label={label}
                                                        {...widgetOffsetFieldProps(field, error, t)}
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        ))}
                                    </>
                                )}
                                {modal.type !== 'popup' && (
                                    <Controller
                                        control={control}
                                        name="backgroundColor"
                                        render={({ field, ref }) => (
                                            <ColorPicker
                                                ref={ref}
                                                inputProps={{
                                                    ...field,
                                                    onChange: e => {
                                                        const val = e?.target?.value ? e.target.value : e;
                                                        field.onChange(val);
                                                    },
                                                }}
                                                label={t('common.background')}
                                            />
                                        )}
                                    />
                                )}
                            </Form>
                            <Controller
                                control={control}
                                name="onlyFeatured"
                                render={({ field }) => (
                                    <ReviewsMode>
                                        <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                                            {t('ShowcaseWidgets.reviewsMode')}
                                        </Typography>
                                        <ReviewsModeGroup
                                            value={field.value ? REVIEWS_MODE.FEATURED : REVIEWS_MODE.TOP_RATED}
                                            onChange={e =>
                                                setValue('onlyFeatured', e.target.value === REVIEWS_MODE.FEATURED, {
                                                    shouldDirty: true,
                                                })
                                            }
                                        >
                                            {[
                                                {
                                                    value: REVIEWS_MODE.TOP_RATED,
                                                    label: t('ShowcaseWidgets.topRatedReviews'),
                                                },
                                                {
                                                    value: REVIEWS_MODE.FEATURED,
                                                    label: t('ShowcaseWidgets.featuredReviews'),
                                                    disabled: noFeaturedReviews,
                                                },
                                            ].map(option => (
                                                <Tooltip
                                                    key={option.value}
                                                    title={
                                                        option.disabled ? t('ShowcaseWidgets.noFeaturedReviews') : ''
                                                    }
                                                    placement="top-start"
                                                >
                                                    <span
                                                        style={{ width: 'fit-content' }}
                                                        tabIndex={option.disabled ? 0 : undefined}
                                                    >
                                                        <FormControlLabel
                                                            value={option.value}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: 'var(--text-secondary)',
                                                                        padding: '6px',
                                                                        '&.Mui-checked': {
                                                                            color: 'var(--primary-main)',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                                    {option.label}
                                                                </Typography>
                                                            }
                                                            disabled={option.disabled}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            ))}
                                        </ReviewsModeGroup>
                                    </ReviewsMode>
                                )}
                            />
                        </>
                    )}
                    {modal.view === view.CODE && modal.open && (
                        <Code widgetKey={account?.widgetKey} type={modal.type} />
                    )}
                </MainContent>
            </FormProvider>
            <SubContent>
                {modal.view === view.FORM && (
                    <WidgetPreview widgetKey={account?.widgetKey} type={modal.type} formValues={formValues} />
                )}
            </SubContent>
        </Modal>
    );
};

export default EditModal;
